// REST client for the v3 /admin API. Adapted from the user webapp's lib/api.ts.
//
// Token policy (LOCKED rules): access_token lives in JS MEMORY ONLY (never
// localStorage/sessionStorage/URL). The backend sets an httpOnly Secure
// SameSite=Strict refresh cookie scoped to Path=/v3/auth/refresh for the console's
// own client (c-ls-admin → refresh_token__c-ls-admin, host-only; restful 00 §A.7
// per-client session isolation). Refresh rides that cookie via credentials:'include' on
// /v3/auth/refresh ONLY; every other request goes out WITHOUT credentials.
//
// Envelope: success {data, request_id} / error {error:{code,message}, request_id}.
// X-Request-Id on every response. All timestamps are MICROSECONDS since epoch.
//
// RBAC: any /admin call may 403 even with a valid token (server-side requireAdmin
// is the real gate). ApiError.status === 403 surfaces as a "無權限" state per view.

import { config } from '../config';

// ---- in-memory token store ----

type TokenListener = (token: string | null) => void;

let accessToken: string | null = null;
const tokenListeners = new Set<TokenListener>();

export const tokenStore = {
  get(): string | null {
    return accessToken;
  },
  set(token: string | null): void {
    accessToken = token;
    for (const fn of tokenListeners) fn(token);
  },
  clear(): void {
    tokenStore.set(null);
  },
  subscribe(fn: TokenListener): () => void {
    tokenListeners.add(fn);
    return () => tokenListeners.delete(fn);
  },
};

// ---- errors ----

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;

  constructor(status: number, code: string, message: string, requestId: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }

  /** Server-side RBAC rejection (requireAdmin) — distinct from 401 session-expiry. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

// ---- unauthorized hook (auth context registers a logout handler) ----

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Register the session-expired handler (refresh failed or second 401). One handler max. */
export function onUnauthorized(fn: UnauthorizedHandler | null): void {
  unauthorizedHandler = fn;
}

// ---- session hint cookie ----
// Non-sensitive boolean marker (NO token material — in-memory-only policy holds).
// The refresh cookie is httpOnly so JS cannot see whether a session exists; without
// a hint every anonymous boot would fire a doomed POST /v3/auth/refresh and log a
// 400 console error.

const HINT = 'ls_admin_auth_hint';

export function hasSessionHint(): boolean {
  return document.cookie.split('; ').some((c) => c === HINT + '=1');
}

function setSessionHint(on: boolean): void {
  document.cookie = on
    ? `${HINT}=1; path=/; max-age=2592000; secure; samesite=strict`
    : `${HINT}=; path=/; max-age=0; secure; samesite=strict`;
}

// ---- single-flight refresh ----

let refreshing: Promise<string> | null = null;

/**
 * POST /v3/auth/refresh — the ONLY call with credentials:'include' (httpOnly
 * refresh cookie, Path=/v3/auth/refresh). X-Client-Id/Key act as the CSRF gate;
 * no Authorization header and no body. Single-flight: concurrent 401s share one
 * in-flight refresh.
 */
export function refreshAccessToken(): Promise<string> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const res = await fetch(config.apiBase + '/v3/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Id': config.clientId,
        'X-Client-Key': config.clientKey,
      },
    });
    const body = await res.json().catch(() => ({}) as Record<string, unknown>);
    if (!res.ok) {
      if (res.status === 400 || res.status === 401) setSessionHint(false);
      const err = (body as { error?: { code?: string; message?: string } }).error;
      throw new ApiError(
        res.status,
        err?.code ?? 'REFRESH_FAILED',
        err?.message ?? `refresh ${res.status}`,
        res.headers.get('X-Request-Id')
      );
    }
    const token = (body as { data?: { access_token?: string } }).data?.access_token;
    if (!token) throw new ApiError(res.status, 'REFRESH_FAILED', 'refresh: no access_token', res.headers.get('X-Request-Id'));
    tokenStore.set(token);
    setSessionHint(true); // renew the hint's max-age alongside the rotated cookie
    return token;
  })();
  refreshing.then(
    () => {
      refreshing = null;
    },
    () => {
      refreshing = null;
    }
  );
  return refreshing;
}

// ---- core fetch ----

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON-serialized request body. */
  body?: unknown;
  /** Query params; null/undefined/'' values are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface ApiResult<T> {
  data: T;
  /** X-Request-Id response header (cite in error toasts for support). */
  requestId: string | null;
  status: number;
}

function buildUrl(path: string, query?: ApiOptions['query']): string {
  const url = new URL(config.apiBase + path);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== null && v !== undefined && v !== '') url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

/**
 * Calls the v3 REST API. Adds X-Client-Id/Key + Bearer (when a token is held),
 * parses the {data}/{error:{code,message}} envelope, and on a 401 performs ONE
 * transparent refresh-and-replay; a second 401 (or failed refresh) clears the
 * token and fires the onUnauthorized handler. A 403 throws ApiError (handle per
 * view — server RBAC may reject any admin call).
 */
export async function apiFetch<T = unknown>(path: string, opts: ApiOptions = {}, _retried = false): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Client-Id': config.clientId,
    'X-Client-Key': config.clientKey,
    ...opts.headers,
  };
  const token = tokenStore.get();
  if (token) headers['Authorization'] = 'Bearer ' + token;

  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    // No credentials here — the refresh cookie is scoped to /v3/auth/refresh only.
  });

  if (res.status === 401 && !_retried) {
    try {
      await refreshAccessToken();
    } catch {
      tokenStore.clear();
      unauthorizedHandler?.();
      throw new ApiError(401, 'UNAUTHORIZED', '登入逾期，請重新登入', res.headers.get('X-Request-Id'));
    }
    return apiFetch<T>(path, opts, true);
  }

  const requestId = res.headers.get('X-Request-Id');
  const body = (await res.json().catch(() => ({}))) as {
    data?: T;
    request_id?: string;
    error?: { code?: string; message?: string };
  };

  if (res.status === 401) {
    tokenStore.clear();
    unauthorizedHandler?.();
    throw new ApiError(401, 'UNAUTHORIZED', '登入逾期，請重新登入', requestId);
  }
  if (!res.ok) {
    throw new ApiError(res.status, body.error?.code ?? `HTTP_${res.status}`, body.error?.message ?? `HTTP ${res.status}`, requestId);
  }

  return {
    data: body.data as T,
    requestId: requestId ?? body.request_id ?? null,
    status: res.status,
  };
}

// ---- cursor pagination helper ----
// FACTS PACK §3: admin browse endpoints return data.next_cursor as an opaque
// base64 string; "" OR an OMITTED field both mean "last page". Pass it back as
// ?cursor=<next_cursor>. This normalizer hides the omitted-vs-empty quirk so
// feature code can just check `if (page.nextCursor)`.

export interface CursorPage<T> {
  items: T[];
  /** null when there are no more pages (covers both "" and an absent field). */
  nextCursor: string | null;
  requestId: string | null;
}

/** Treat ""/undefined/null next_cursor as "no more pages". */
export function normalizeCursor(raw: string | null | undefined): string | null {
  return raw && raw.length > 0 ? raw : null;
}

// ---- auth endpoints ----

export interface AdminUserClaims {
  user_id?: string;
  email?: string;
  display_name?: string;
  region?: string;
  role?: string;
  status?: string;
  created_at?: number;
  [k: string]: unknown;
}

export interface LoginData {
  access_token: string;
  expires_in: number;
  client_capabilities?: Record<string, unknown>;
  user?: AdminUserClaims;
}

/** POST /v3/auth/login. credentials:'include' so the browser STORES the httpOnly refresh cookie. */
export async function login(email: string, password: string): Promise<LoginData> {
  const res = await fetch(config.apiBase + '/v3/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Id': config.clientId,
      'X-Client-Key': config.clientKey,
    },
    body: JSON.stringify({ email, password }),
  });
  const body = (await res.json().catch(() => ({}))) as {
    data?: LoginData;
    error?: { code?: string; message?: string };
  };
  if (!res.ok || !body.data?.access_token) {
    throw new ApiError(res.status, body.error?.code ?? `HTTP_${res.status}`, body.error?.message ?? `HTTP ${res.status}`, res.headers.get('X-Request-Id'));
  }
  tokenStore.set(body.data.access_token);
  setSessionHint(true);
  return body.data;
}

/** POST /v3/auth/logout — clears the refresh cookie server-side, then drops the in-memory token. */
export async function logout(): Promise<void> {
  try {
    await fetch(config.apiBase + '/v3/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Id': config.clientId,
        'X-Client-Key': config.clientKey,
        ...(tokenStore.get() ? { Authorization: 'Bearer ' + tokenStore.get() } : {}),
      },
    });
  } catch {
    // network failure on logout is non-fatal; the in-memory token dies regardless
  } finally {
    tokenStore.clear();
    setSessionHint(false);
  }
}

// ---- JWT claim decode (display only — NEVER trust for security) ----
// LOCKED rule: no client-side JWT verification. We decode the payload purely to
// read the `role` claim so the SPA can gate which nav items / pages to SHOW.
// Real enforcement is server-side requireAdmin. A forged token only changes the
// UI; every privileged call still 401/403s at the edge.

export interface JwtClaims {
  role?: string;
  scope?: string[];
  region?: string;
  client_id?: string;
  exp?: number;
  [k: string]: unknown;
}

/** Best-effort decode of a JWT payload segment. Returns null on any malformed input. */
export function decodeJwtClaims(token: string | null): JwtClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(decodeURIComponent(escape(atob(padded)))) as JwtClaims;
  } catch {
    return null;
  }
}
